import { View } from "@/components/ui";
import {
  User,
  CaretRight,
  Invoice,
  CreditCard,
  FileText,
  ShieldCheck,
  ClipboardText,
  BellSimple,
  Users,
} from "@/components/shared/icons";

import { Typography } from "@/components/shared/typography";
import { WhatsappSupportBanner } from "@/components/shared/whatsapp-support-banner";
import { useRoute } from "@/components/shared/router";
import type { PushParams } from "@/types/global/route";
import { links } from "@/constants";
import { onChatSupport } from "@/lib/chat-support";
import { handleOpenLink } from "@/lib/platform";

enum AccountOverviewNavigateEnum {
  AccountInfo = "Account_Info",
  AccountServiceProfile = "Account_Service_Profile",
  AccountInvoices = "Account_Invoices",
  AccountPayment = "Account_Payment",
  AccountTermOfServices = "AccountTermOfServices",
  AccountPrivacyPolicy = "AccountPrivacyPolicy",
  AccountNotifications = "AccountNotifications",
  AccountMyReferral = "AccountMyReferral",
}

const ACCOUNT_SETTINGS_LIST = [
  {
    icon: User,
    label: "Account Info",
    route: AccountOverviewNavigateEnum.AccountInfo,
    pageKey: "accountInfo" as const,
  },

  {
    icon: ClipboardText,
    label: "Services Profile",
    route: AccountOverviewNavigateEnum.AccountServiceProfile,
    pageKey: "serviceProfile" as const,
  },
  {
    icon: BellSimple,
    label: "Notifications",
    route: AccountOverviewNavigateEnum.AccountNotifications,
    pageKey: "notificationSettings" as const,
  },
];

const BILLING_SETTINGS_LIST = [
  {
    icon: Invoice,
    label: "Invoices",
    route: AccountOverviewNavigateEnum.AccountInvoices,
    pageKey: "invoices" as const,
  },
  {
    icon: CreditCard,
    label: "Payment",
    route: AccountOverviewNavigateEnum.AccountPayment,
    pageKey: "payment" as const,
  },
];

const OTHER_SETTINGS_LIST = [
  {
    icon: Users,
    label: "My Referral",
    route: AccountOverviewNavigateEnum.AccountMyReferral,
    pageKey: "accountMyReferral" as const,
  },
  {
    icon: FileText,
    label: "Terms of Services",
    route: AccountOverviewNavigateEnum.AccountTermOfServices,
    isExternal: true,
    link: links.legalTermCondition,
  },
  {
    icon: ShieldCheck,
    label: "Privacy Policy",
    route: AccountOverviewNavigateEnum.AccountPrivacyPolicy,
    isExternal: true,
    link: links.legalPrivacyPolicy,
  },
];

export const AccountOverview = () => {
  const { push } = useRoute();
  const onClickNavigate = (pageKey: PushParams["pageKey"]) => {
    push({ pageKey });
  };
  return (
    <View className="flex flex-col gap-2 rounded-xl bg-background-intermediate p-2 md:hidden">
      <View className="rounded-lg bg-white">
        <Typography
          variant="label-md"
          color="foreground-intermediate"
          className="px-4 py-3"
        >
          Account &amp; Settings
        </Typography>
        {ACCOUNT_SETTINGS_LIST.map((item, index) => (
          <View key={item.route} onClick={() => onClickNavigate(item.pageKey)}>
            <View className="flex flex-row items-center gap-3 px-4 py-3">
              <View className="flex flex-1 flex-row items-center gap-3">
                <item.icon
                  size={24}
                  className="size-6 text-foreground-intermediate"
                />
                <Typography variant="label-sm" color="foreground-default">
                  {item.label}
                </Typography>
              </View>
              <CaretRight className="text-foreground-intermediate" size={16} />
            </View>
            {index !== ACCOUNT_SETTINGS_LIST.length - 1 && (
              <View className="h-px w-full bg-border" />
            )}
          </View>
        ))}
      </View>
      <View className="rounded-lg bg-white">
        <Typography
          variant="label-md"
          color="foreground-intermediate"
          className="px-4 py-3"
        >
          Billing
        </Typography>
        {BILLING_SETTINGS_LIST.map((item, index) => (
          <View key={item.route} onClick={() => onClickNavigate(item.pageKey)}>
            <View className="flex flex-row items-center gap-3 px-4 py-3">
              <View className="flex flex-1 flex-row items-center gap-3">
                <item.icon className="size-6 text-foreground-intermediate" />
                <Typography variant="label-sm" color="foreground-default">
                  {item.label}
                </Typography>
              </View>
              <CaretRight className="text-foreground-intermediate" size={16} />
            </View>
            {index !== BILLING_SETTINGS_LIST.length - 1 && (
              <View className="h-px w-full bg-border" />
            )}
          </View>
        ))}
      </View>
      <WhatsappSupportBanner onClick={() => onChatSupport()} />
      <View className="rounded-lg bg-white">
        <Typography
          variant="label-md"
          color="foreground-intermediate"
          className="px-4 py-3"
        >
          Other Info
        </Typography>
        {OTHER_SETTINGS_LIST.map((item, index) => (
          <View
            key={item.route}
            onClick={() => {
              if ("isExternal" in item && item.isExternal) {
                handleOpenLink(item.link);
              } else if ("pageKey" in item && item.pageKey) {
                onClickNavigate(item.pageKey as PushParams["pageKey"]);
              }
            }}
          >
            <View className="flex flex-row items-center gap-3 px-4 py-3">
              <View className="flex flex-1 flex-row items-center gap-3">
                <item.icon className="size-6 text-foreground-intermediate" />
                <Typography variant="label-sm" color="foreground-default">
                  {item.label}
                </Typography>
              </View>
              <CaretRight className="text-foreground-intermediate" size={16} />
            </View>
            {index !== OTHER_SETTINGS_LIST.length - 1 && (
              <View className="h-px w-full bg-border" />
            )}
          </View>
        ))}
      </View>
    </View>
  );
};
